/* eslint-disable react-hooks/exhaustive-deps */
import { makeStyles } from '@mui/styles';
import { green } from 'apollo-react/colors';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import IconButton from 'apollo-react/components/IconButton';
import Table, { compareDates, compareNumbers, compareStrings } from 'apollo-react/components/Table';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import ChevronDown from 'apollo-react-icons/ChevronDown';
import ChevronRight from 'apollo-react-icons/ChevronRight';
import Copy from 'apollo-react-icons/Copy';
import Download from 'apollo-react-icons/Download';
import StatusCheck from 'apollo-react-icons/StatusCheck';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomComponentsModal from './CustomComponentsModal';
import TerminalLogs from './TerminalLogs';
import { createUpgradeLogFileName, getApplicationManagementUrl } from './utils';
import { WarningModal } from './WarningModal';
import { addSuccess } from '../../actions/SuccessActions';
import {
  changeFetchingData,
  clearUpgradeModulesStore,
  getUpgradeModules,
  resetModuleUpgrade,
  setModuleWithInitialInProgressStatus,
  STATUSES,
  upgradeModuleStatus,
} from '../../actions/UpgradeModulesActions';
import { getUpgradeLogPackage } from '../../api/upgrade-modules';
import DateCell from '../../components/CommonTableCells/DateCell';
import StatusCell from '../../components/CommonTableCells/StatusCell';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getToken } from '../../form-designer/selectors/common.selectors';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import {
  getCurrentUpgradeModuleId,
  getFormConversionLogFile,
  getModuleWithInitialInProgressStatus,
  isFetchingModuleSelector,
} from '../../selectors/UpgradeModulesSelectors';
import { downloadFile } from '../../utils/domUtils';

const useStyles = makeStyles({
  logTitle: {
    margin: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  circularProgress: {
    '& > div': {
      display: 'flex',
    },
  },
});

const ExpandCell = ({ row: { moduleId, handleToggleRow, expanded, currentUpgradeModuleId } }) => {
  return (
    <div style={{ width: 12 }}>
      <Tooltip title={expanded ? 'Collapse' : 'Expand'} disableFocusListener>
        <span>
          <IconButton
            id="expand"
            size="small"
            disabled={moduleId === currentUpgradeModuleId}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => handleToggleRow(moduleId)}
          >
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

const Cell = ({ row, column }) => <div>{row[column.accessor]}</div>;

const CustomComponentsCountCell = ({ row, column }) => {
  const { moduleId, setOpenModal, setOpenedModuleId } = row;

  return (
    <Button
      onClick={() => {
        setOpenModal(true);
        setOpenedModuleId(moduleId);
      }}
      variant="text"
    >
      {row[column.accessor]}
    </Button>
  );
};

const UpgradeModuleCell = ({
  row: {
    moduleId,
    isFetchingModule,
    currentUpgradeModuleId,
    timeoutRef,
    stopPoolingRef,
    handleToggleRow,
    setWarningModalOpen,
    status,
  },
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const upgradeModuleHandler = () => {
    handleToggleRow(moduleId, true);
    stopPoolingRef.current = false;
    dispatch(getUpgradeModules({ moduleId, timeoutRef, stopPoolingRef }));
  };

  const isModuleInProgress = currentUpgradeModuleId === moduleId;

  return status !== STATUSES.COMPLETED ? (
    <>
      <Button
        variant="secondary"
        size="small"
        style={{ width: '75px' }}
        onClick={upgradeModuleHandler}
        disabled={isFetchingModule}
      >
        {isModuleInProgress ? (
          <CircularProgress
            className={classes.circularProgress}
            color="inherit"
            variant="indeterminate"
            size={18}
          />
        ) : (
          translateString('Upgrade')
        )}
      </Button>
      {isModuleInProgress && (
        <Button
          variant="secondary"
          size="small"
          style={{ marginLeft: 15 }}
          onClick={() => setWarningModalOpen(true)}
        >
          {translateString('Reset')}
        </Button>
      )}
    </>
  ) : (
    <StatusCheck style={{ color: green }} />
  );
};

const DetailRow = ({ row }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);

  const onCopyClickHandler = () => {
    const terminalLogs = row.logs.join('\n');

    navigator.clipboard.writeText(terminalLogs);
    dispatch(addSuccess({ message: translateString('Logs were copied successfully') }));
  };

  const onUpgradeLogClickHandler = async () => {
    setLoading(true);
    const fileName = createUpgradeLogFileName(row.name, row.version);

    const response = await getUpgradeLogPackage(row.moduleId);
    downloadFile(fileName, response, 2);
    setLoading(false);
  };

  const isUpgradeLogVisible = row.status === STATUSES.COMPLETED || row.status === STATUSES.ERROR;

  return (
    <>
      <div className={classes.logTitle}>
        <Typography>{translateString('Logs output:')}</Typography>
        <div>
          <Tooltip title={row.logs.length === 0 ? null : translateString('Copy logs')}>
            <IconButton onClick={onCopyClickHandler} disabled={row.logs.length === 0}>
              <Copy />
            </IconButton>
          </Tooltip>
          {isUpgradeLogVisible && (
            <Button
              variant="secondary"
              disabled={isLoading}
              style={{ marginLeft: 15 }}
              icon={Download}
              onClick={onUpgradeLogClickHandler}
            >
              {translateString('Upgrade Log')}
            </Button>
          )}
        </div>
      </div>
      <TerminalLogs logs={row.logs} />
    </>
  );
};

const MemorizedDetailRow = React.memo(DetailRow, equalityFn);

const columns = [
  {
    accessor: 'expand',
    customCell: ExpandCell,
  },
  {
    header: 'Name',
    accessor: 'name',
    sortFunction: compareStrings,
    customCell: Cell,
  },
  {
    header: 'Version',
    accessor: 'version',
    sortFunction: compareStrings,
    customCell: Cell,
  },
  {
    header: 'Upgrade To Version',
    accessor: 'upgradeToVersion',
    sortFunction: compareStrings,
    customCell: Cell,
  },
  {
    header: 'Modified Date',
    accessor: 'modifiedDate',
    sortFunction: compareDates,
    customCell: DateCell,
  },
  {
    header: 'Modified By',
    accessor: 'modifiedBy',
    sortFunction: compareStrings,
    customCell: Cell,
  },
  {
    header: 'Custom Components',
    accessor: 'customComponentsCount',
    sortFunction: compareNumbers,
    customCell: CustomComponentsCountCell,
  },
  {
    header: 'Status',
    accessor: 'status',
    sortFunction: compareStrings,
    customCell: StatusCell,
  },
  {
    header: 'Upgrade',
    accessor: 'upgrade',
    sortFunction: compareStrings,
    customCell: UpgradeModuleCell,
  },
];

const CustomHeader = () => {
  const translateString = useTranslation();
  const token = useSelector(getToken);

  const { FileName: fileName, FileDataBase64: fileDataBase64 } = useSelector(
    getFormConversionLogFile
  );

  const isDisabled = !fileName || !fileDataBase64;

  const downloadClickHandler = () => {
    const decoded = Buffer.from(fileDataBase64, 'base64').toString();
    downloadFile(fileName, decoded, 1);
  };

  const url = getApplicationManagementUrl(token);

  return (
    <div>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Button variant="text" style={{ marginRight: 15 }}>
          {translateString('Application Migration History Log')}
        </Button>
      </a>
      <Button
        variant="secondary"
        icon={Download}
        onClick={downloadClickHandler}
        disabled={isDisabled}
      >
        {translateString('Download Forms Conversion Log')}
      </Button>
    </div>
  );
};

const UpgradeModulesTable = ({ modules, isLoading }) => {
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const isFetchingModule = useSelector(isFetchingModuleSelector);
  const currentUpgradeModuleId = useSelector(getCurrentUpgradeModuleId);
  const [expandedRows, setExpandedRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openedModuleId, setOpenedModuleId] = useState(null);
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const timeoutRef = useRef(null);
  const stopPoolingRef = useRef(false);
  const inProgressModule = useSelector(getModuleWithInitialInProgressStatus);

  const handleToggleRow = (moduleId, forceExpand) => {
    setExpandedRows((expandedRows) => {
      if (forceExpand && expandedRows.includes(moduleId)) {
        return expandedRows;
      }

      return expandedRows.includes(moduleId)
        ? expandedRows.filter((id) => id !== moduleId)
        : [...expandedRows, moduleId];
    });
  };

  const handleResetModule = () => {
    dispatch(resetModuleUpgrade(currentUpgradeModuleId, stopPoolingRef, timeoutRef));
    setWarningModalOpen(false);
  };

  useEffect(() => {
    return () => {
      stopPoolingRef.current = true;
      clearInterval(timeoutRef.current);
      dispatch(clearUpgradeModulesStore());
    };
  }, []);

  useEffect(() => {
    if (inProgressModule) {
      changeFetchingData(true, inProgressModule.Id, dispatch);
      handleToggleRow(inProgressModule.Id, true);
      upgradeModuleStatus(inProgressModule.Id, timeoutRef, stopPoolingRef, dispatch);
      dispatch(setModuleWithInitialInProgressStatus(null));
    }
  }, [inProgressModule]);

  return (
    <>
      <CustomComponentsModal open={openModal} moduleId={openedModuleId} setOpen={setOpenModal} />
      <Table
        title="Modules list"
        columns={columns}
        rowId="moduleId"
        emptyProps={{
          text: translateString('No data to display'),
        }}
        rows={modules.map((row) => ({
          ...row,
          expanded: expandedRows.includes(row.moduleId),
          isFetchingModule,
          currentUpgradeModuleId,
          timeoutRef,
          stopPoolingRef,
          setOpenModal,
          setOpenedModuleId,
          handleToggleRow,
          setWarningModalOpen,
        }))}
        initialSortedColumn="name"
        initialSortOrder="asc"
        rowsPerPageOptions={['All']}
        rowProps={{ hover: false }}
        isLoading={isLoading}
        tablePaginationProps={{
          labelDisplayedRows: ({ from, to, count }) =>
            `${count === 1 ? 'Module' : 'Modules'} ${from}-${to} of ${count}`,
          truncate: true,
        }}
        ExpandableComponent={MemorizedDetailRow}
        CustomHeader={() => <CustomHeader />}
      />
      <WarningModal
        open={isWarningModalOpen}
        setWarningModalOpen={setWarningModalOpen}
        onConfirmClick={handleResetModule}
      />
    </>
  );
};

export default UpgradeModulesTable;
